<!-- 余额退款 -->

<template>
    <div class="balance-refund-dialog">
        <w-dialog ref="dialogRef" title="余额退款" width="30%" top="15vh" confirmText="确认退款" @wConfirm="handleRefund"
            :loading="isSubmitting">
            <!-- 订单信息卡片 -->
            <div class="order-card" v-if="orderInfo">
                <div class="card-header">
                    <i class="iconfont icon-a-lujing207"></i>
                    <span>订单信息</span>
                </div>
                <div class="card-content">
                    <div class="info-row">
                        <span class="label">会员余额</span>
                        <span class="value">{{ orderInfo.member_surplus_money }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">订单金额</span>
                        <span class="value">{{ orderInfo.mwr_money }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">已提现金额</span>
                        <span class="value">{{ orderInfo.withdrawal_money }}</span>
                    </div>
                    <div class="info-row highlight">
                        <span class="label">可退金额</span>
                        <span class="value refundable">{{ refundableAmount }}</span>
                    </div>
                </div>
            </div>

            <!-- 退款金额输入 -->
            <div class="refund-input-section" v-if="orderInfo">
                <div class="section-title">
                    <i class="iconfont icon-a-lujing434"></i>
                    <span>退款金额</span>
                </div>
                <div class="input-wrapper">
                    <el-input-number v-model="refundForm.refundAmount" :min="0.01" :max="refundableAmount"
                        :precision="2" :step="0.01" placeholder="请输入退款金额" size="large" class="refund-input" />
                </div>
            </div>
        </w-dialog>
    </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Member } from "@/plugins/api.js";
import wDialog from "@/components/w-dialog/w-dialog.vue";

// Props 定义
const props = defineProps({
    orderInfo: {
        type: Object,
        default: null
    }
});

// 响应式数据
const dialogRef = ref(null);
const isSubmitting = ref(false);

// 退款表单数据
const refundForm = ref({
    refundAmount: 0
});

// 计算可退金额
const refundableAmount = computed(() => {
    if (!props.orderInfo) return 0;
    const surplus_money = Number(props.orderInfo.member_surplus_money) || 0;
    const orderAmount = Number(props.orderInfo.mwr_money) || 0;
    const withdrawnAmount = Number(props.orderInfo.withdrawal_money) || 0;
    return Math.max(0, (surplus_money > orderAmount ? orderAmount : surplus_money) - withdrawnAmount);
});

// 监听订单信息变化，重置表单
watch(() => props.orderInfo, (newOrderInfo) => {
    if (newOrderInfo) {
        refundForm.value = {
            refundAmount: refundableAmount.value
        };
    }
}, { immediate: true });

/**
 * 处理退款
 */
const handleRefund = async () => {
    // 简单验证
    if (!refundForm.value.refundAmount || refundForm.value.refundAmount <= 0) {
        ElMessage.error('请输入有效的退款金额');
        return;
    }

    if (refundForm.value.refundAmount > refundableAmount.value) {
        ElMessage.error('退款金额不能超过可退金额');
        return;
    }

    try {
        // 确认对话框
        await ElMessageBox.confirm(
            `确认退款 ¥${refundForm.value.refundAmount} 吗？`,
            '退款确认',
            {
                confirmButtonText: '确认',
                cancelButtonText: '取消',
                type: 'warning'
            }
        );

        isSubmitting.value = true;

        // 调用退款API
        const response = await Member.balanceRefund({
            order_id: props.orderInfo.or_id,
            m_id: props.orderInfo.m_id,
            refund_money: refundForm.value.refundAmount
        });

        if (response.Code === 200) {
            ElMessage.success(response.Message || '退款申请提交成功');
            close();
            // 触发父组件刷新
            emit('refundSuccess');
        } else {
            ElMessage.error(response.Message || '退款申请失败');
        }
    } catch (error) {
        if (error !== 'cancel') {
            console.error('退款失败:', error);
            ElMessage.error('退款申请失败，请重试');
        }
    } finally {
        isSubmitting.value = false;
    }
};

/**
 * 打开对话框
 */
const show = () => {
    dialogRef.value?.show();
};

/**
 * 关闭对话框
 */
const close = () => {
    dialogRef.value?.close();
    // 重置表单
    refundForm.value = {
        refundAmount: 0
    };
};

// 定义事件
const emit = defineEmits(['refundSuccess']);

// 暴露方法给父组件
defineExpose({
    show,
    close
});
</script>

<style lang="scss" scoped>
.balance-refund-dialog {
    .order-card {
        background: #f3f4f8;
        border-radius: 8px;
        margin-bottom: 20px;
        overflow: hidden;

        .card-header {
            background: var(--theme-color);
            color: white;
            padding: 12px 16px;
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 600;

            i {
                margin-right: 8px;
                font-size: 16px;
            }
        }

        .card-content {
            padding: 16px;

            .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px solid #e5e7eb;

                &:last-child {
                    border-bottom: none;
                }

                &.highlight {
                    background: rgba(26, 201, 148, 0.1);
                    margin: 0 -16px;
                    padding: 12px 16px;
                    border-radius: 0 0 8px 8px;
                    border-bottom: none;
                }

                .label {
                    font-size: 14px;
                    color: #666;
                    font-weight: 500;
                }

                .value {
                    font-size: 14px;
                    color: #333;
                    font-weight: 600;

                    &.refundable {
                        color: var(--error-color);
                        font-size: 16px;
                    }
                }
            }
        }
    }

    .refund-input-section {
        margin-bottom: 24px;

        .section-title {
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: 600;
            color: #333;
            margin-bottom: 16px;

            i {
                margin-right: 8px;
                color: var(--theme-color);
                font-size: 18px;
            }
        }

        .input-wrapper {
            .refund-input {
                width: 100%;

                :deep(.el-input-number) {
                    width: 100%;
                }

                :deep(.el-input__inner) {
                    height: 48px;
                    font-size: 16px;
                    font-weight: 600;
                    text-align: center;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    transition: all 0.3s ease;

                    &:focus {
                        border-color: var(--theme-color);
                        box-shadow: 0 0 0 3px rgba(26, 201, 148, 0.1);
                    }
                }

                :deep(.el-input-number__decrease),
                :deep(.el-input-number__increase) {
                    height: 45px;
                    line-height: 45px;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    background: #f8f9fa;
                    color: #666;
                    font-size: 16px;
                    font-weight: 600;
                    transition: all 0.3s ease;

                    &:hover {
                        background: var(--theme-color);
                        color: white;
                        border-color: var(--theme-color);
                    }

                    &:active {
                        background: #15a085;
                        border-color: #15a085;
                    }
                }

                :deep(.el-input-number__decrease) {
                    border-right: none;
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                }

                :deep(.el-input-number__increase) {
                    border-left: none;
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                }

                :deep(.el-input-number__decrease.is-disabled),
                :deep(.el-input-number__increase.is-disabled) {
                    background: #f5f7fa;
                    color: #c0c4cc;
                    border-color: #e4e7ed;
                    cursor: not-allowed;

                    &:hover {
                        background: #f5f7fa;
                        color: #c0c4cc;
                        border-color: #e4e7ed;
                    }
                }
            }
        }
    }
}
</style>
